/**
 * Undo Gizmo Service
 *
 * Inspector V2 service that hooks into Babylon.js gizmo drag observables
 * to capture before/after transform snapshots for undo/redo.
 *
 * Uses the same prototype patching pattern as planar-gizmo-service.ts:
 * patches the `attachedNode` setter on each gizmo class to subscribe
 * to onDragStartObservable / onDragEndObservable when a node is attached.
 */

import type { ServiceDefinition, IGizmoService } from '@babylonjs/inspector';
import { GizmoServiceIdentity } from '@babylonjs/inspector';
import { PositionGizmo } from '@babylonjs/core/Gizmos/positionGizmo';
import { RotationGizmo } from '@babylonjs/core/Gizmos/rotationGizmo';
import { ScaleGizmo } from '@babylonjs/core/Gizmos/scaleGizmo';
import { BoundingBoxGizmo } from '@babylonjs/core/Gizmos/boundingBoxGizmo';
import { getUndoManager, TransformCommand, type TransformSnapshot } from 'trace-viewer/editor';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from 'trace-viewer/scene';
import { Properties } from 'trace-viewer/behaviors';
import { captureTransformForKeyframe, getKeyframeCaptureContext } from './keyframe-capture';
import { captureTransformForState } from './state-capture';
import { captureTransformForPage } from './page-editing-mode';
import { isGroupDragActive } from './group-drag-state';
import { copyForInsideEdit, needsReferenceCopy } from './cms/reference-edit-guard';
import { showToast } from './toast';

// ============================================================================
// Per-instance observer tracking
// ============================================================================

/** Minimal interface for observer cleanup — avoids Observer<T> invariance issues. */
interface Removable { remove(): void }

/** Observable that supports adding a no-arg callback. */
interface HookableObservable { add(callback: () => void): Removable | null }

interface GizmoObservers {
    dragStart: Removable;
    dragEnd: Removable[];
}

const hookedInstances = new Map<object, GizmoObservers>();

let beforeSnapshot: TransformSnapshot | null = null;
/**
 * The dragged object's WORLD pose before the drag.
 *
 * A local snapshot is only meaningful in the frame it was taken in, and a
 * copy-on-inside-edit can change that frame: unpack promotes the placement's
 * child, which absorbs the placement's own transform. The world pose is what
 * survives the move, so the undo entry's `before` is re-derived from it in
 * whatever frame the object ends up in ({@link localSnapshotFromWorld}).
 */
let beforeWorld: Matrix | null = null;
let dragObject: SceneObject | null = null;

function onDragStart(): void {
    // A group transform drag records the whole selection as one CompositeCommand;
    // don't also capture the primary object here (would double-record).
    if (isGroupDragActive()) return;

    const traceScene = getUndoManager().traceScene;
    const selected = traceScene?.getSelectedObject();
    if (!selected) return;

    dragObject = selected;
    beforeSnapshot = TransformCommand.capture(selected);
    beforeWorld = selected.transform.computeWorldMatrix(true).clone();
}

function onDragEnd(): void {
    if (!beforeSnapshot || !dragObject) {
        beforeSnapshot = null;
        beforeWorld = null;
        dragObject = null;
        return;
    }

    const traceScene = getUndoManager().traceScene;
    const selected = traceScene?.getSelectedObject();

    if (selected !== dragObject) {
        beforeSnapshot = null;
        beforeWorld = null;
        dragObject = null;
        return;
    }

    const afterSnapshot = TransformCommand.capture(selected);

    // Only push if transform actually changed
    const b = beforeSnapshot;
    const a = afterSnapshot;
    const changed =
        b.position[0] !== a.position[0] || b.position[1] !== a.position[1] || b.position[2] !== a.position[2] ||
        b.rotation[0] !== a.rotation[0] || b.rotation[1] !== a.rotation[1] || b.rotation[2] !== a.rotation[2] || b.rotation[3] !== a.rotation[3] ||
        b.scale[0] !== a.scale[0] || b.scale[1] !== a.scale[1] || b.scale[2] !== a.scale[2];

    if (changed && !getUndoManager().isUndoRedoing) {
        // Moving a node that belongs to a placed reference is an edit inside
        // it (SCENE_ROOT_SPEC §2.3 "Editing") — moving the PLACEMENT is free,
        // which is what `needsReferenceCopy` distinguishes. The gizmo has
        // already written the pose, so the copy, the undo entries and the revert
        // on failure all belong together: see `finishDragInsideReference`.
        //
        // It does not reach `routeDragEndCapture` either: a keyframe or state
        // capture keyed on a frame the copy is about to replace would store a
        // pose that means nothing afterwards.
        if (needsReferenceCopy(selected)) {
            void finishDragInsideReference(selected, beforeSnapshot, afterSnapshot, beforeWorld);
        } else {
            const capturedIntoKeyframe = routeDragEndCapture(selected, beforeSnapshot);
            if (!capturedIntoKeyframe) {
                getUndoManager().push(new TransformCommand(dragObject, beforeSnapshot, afterSnapshot));
            }
        }
    }

    beforeSnapshot = null;
    beforeWorld = null;
    dragObject = null;
}

/**
 * Finish a drag that moved a node belonging to a placed reference
 * (SCENE_ROOT_SPEC §2.3 "Editing"): copy the asset into this scene, then
 * record the move against the node the scene now owns.
 *
 * The gizmo has already written the new pose by the time this runs, so the two
 * outcomes are not symmetric and neither may be left implicit:
 *
 * - **the copy succeeded** — the content is the scene's own, the unpack
 *   preserved the world pose, and the drag stands. The copy is its own undo
 *   entry, and the move is the next one, with both its poses taken in the
 *   frame the object ended up in (the promoted child absorbs the placement's
 *   transform, so a LOCAL pose captured under the placement would point
 *   somewhere the user never dragged from or to). Undoing once moves the object back; undoing again puts the
 *   reference back — the same order every other guarded path has.
 * - **the copy failed** — the reference is intact, so the pose the gizmo wrote
 *   sits on a node of another document and no save would carry it. It is
 *   reverted and the failure is said out loud rather than left on screen as a
 *   move that silently will not persist. No undo entry: nothing changed.
 *
 * Exported for the same reason `routeDragEndCapture` is — the Babylon gizmo
 * observables that drive `onDragEnd` cannot run headlessly.
 */
export async function finishDragInsideReference(
    moved: SceneObject,
    before: TransformSnapshot,
    after: TransformSnapshot,
    worldBeforeDrag: Matrix | null,
): Promise<void> {
    console.log(`[ReferenceEdit] "${moved.name}" was moved inside a reference — copying it into this scene`);

    if (await copyForInsideEdit(moved)) {
        // Both ends in the frame the object is in now: `after` was captured
        // under the placement, which the copy has just folded away.
        const beforeHere = worldBeforeDrag
            ? localSnapshotFromWorld(worldBeforeDrag, moved)
            : before;
        getUndoManager().push(new TransformCommand(moved, beforeHere, TransformCommand.capture(moved)));
        return;
    }

    // Put it back exactly the way an undo would, including the authored-data
    // fold — then say so.
    new TransformCommand(moved, before, after).undo();
    showToast(
        `"${moved.name}" is part of a placed asset that could not be copied into this scene — the move was undone`,
        'error',
    );
}

/**
 * `world` expressed in the frame `object` is in NOW — Babylon composes
 * `world = local × parentWorld`, so the local pose is the world pose times the
 * inverse of the parent's.
 */
function localSnapshotFromWorld(world: Matrix, object: SceneObject): TransformSnapshot {
    const parent = object.transform.parent;
    const local = parent ? world.multiply(Matrix.Invert(parent.getWorldMatrix())) : world;
    const position = new Vector3();
    const scale = new Vector3();
    const rotation = new Quaternion();
    local.decompose(scale, rotation, position);
    return {
        position: [position.x, position.y, position.z],
        rotation: [rotation.x, rotation.y, rotation.z, rotation.w],
        scale: [scale.x, scale.y, scale.z],
    };
}

/**
 * Route a completed drag's final pose to the correct store, and return whether
 * it was captured (so the caller can decide whether to also push a normal undo
 * command). In order: a keyframe selected on self-or-ancestor, the showing
 * page (for shared content), a showing state, else true Base, where the live
 * pose folds into authored sceneData.
 *
 * Exported so the routing decision is exercised directly by tests rather than
 * re-implemented there — the Babylon gizmo observables that drive `onDragEnd`
 * can't run headlessly, but this function carries the actual Base-vs-keyframe
 * logic that both the leak fixes depend on.
 */
export function routeDragEndCapture(selected: SceneObject, before?: TransformSnapshot): boolean {
    // If a keyframe is selected on an ancestor container, the drag is stored in
    // that keyframe instead of authored data / normal undo.
    if (captureTransformForKeyframe(selected)) return true;

    // Distinguish "true Base" from "a keyframe is selected but the drag wasn't
    // captured into it" (e.g. selection still lagging the resolved data, or no
    // usable entry path). Folding the live pose into authored is ONLY safe at
    // true Base — there the live node IS the authored pose. With a keyframe
    // active the live node is (authored + keyframe delta), so folding it would
    // bake the keyframe move into the base pose ("moving a cube in a keyframe
    // also moved the base").
    if (getKeyframeCaptureContext(selected) !== null) return false;

    // If the object is a SHARED child of a Pages container and a page is
    // showing (page editing on), store the drag in THAT PAGE's state — lazily
    // keying the child on first edit. Page content itself falls through. The
    // page comes before a showing state, as in trace-viewer's `setTransforms`
    // (so `set_transform` and a drag agree): the state showing is usually the
    // page's own, and the page route forks one two pages share where the state
    // route would edit both.
    if (captureTransformForPage(selected, before)) return true;

    // If a GROUP STATE is active (not Base), store the drag in that state
    // instead of folding into authored data — the Properties analog of the
    // keyframe capture above: at a non-Base state the live node carries the
    // state's pose, so folding it would bake that into Base.
    // (captureTransformForState returns true iff a non-Base state is active.)
    if (captureTransformForState(selected)) return true;

    // Reset Properties' saved transform so state overrides use the new position
    // as the authored base.
    selected.getBehavior(Properties)?.resetSavedTransform();

    // Fold the live (dragged) pose into authored sceneData. The gizmo path
    // otherwise leaves _sceneObjectData.transform stale — the new pose lives only
    // on the Babylon node. That's invisible until the object has a Properties
    // behavior (e.g. auto-attached by a prior keyframe edit): the next keyframe
    // navigation re-seeds Properties' _savedTransform from the STALE authored
    // pose via _ensureSavedTransform, and reverting to Base then restores that
    // stale pose — silently dropping the Base drag. captureTransform must run
    // AFTER resetSavedTransform so getAuthoredTransform() returns undefined and
    // captureTransform reads the live node.
    selected.captureTransform();
    return false;
}

export function hookGizmoInstance(gizmo: { onDragStartObservable: HookableObservable; onDragEndObservable?: HookableObservable } | BoundingBoxGizmo): void {
    if (hookedInstances.has(gizmo)) return;

    const dragStart = gizmo.onDragStartObservable.add(onDragStart);
    if (!dragStart) return;

    // BoundingBoxGizmo has no onDragEndObservable — it uses separate
    // onScaleBoxDragEndObservable and onRotationSphereDragEndObservable instead.
    const dragEnd: Removable[] = [];
    if ('onDragEndObservable' in gizmo && gizmo.onDragEndObservable) {
        const obs = gizmo.onDragEndObservable.add(onDragEnd);
        if (obs) dragEnd.push(obs);
    } else if (gizmo instanceof BoundingBoxGizmo) {
        const s = gizmo.onScaleBoxDragEndObservable.add(onDragEnd);
        const r = gizmo.onRotationSphereDragEndObservable.add(onDragEnd);
        if (s) dragEnd.push(s);
        if (r) dragEnd.push(r);
    }

    if (dragEnd.length > 0) {
        hookedInstances.set(gizmo, { dragStart, dragEnd });
    }
}

// ============================================================================
// Prototype patching
// ============================================================================

interface PatchState {
    originalDescriptor: PropertyDescriptor;
    prototype: object;
    property: string;
}

const patches: PatchState[] = [];

function patchGizmoClass(
    GizmoClass: abstract new (...args: never[]) => unknown,
    property: 'attachedNode' | 'attachedMesh' = 'attachedNode'
): void {
    const proto = GizmoClass.prototype as object;
    const descriptor = Object.getOwnPropertyDescriptor(proto, property);
    if (!descriptor?.set) return;

    const origDesc = descriptor;
    patches.push({ originalDescriptor: descriptor, prototype: proto, property });

    Object.defineProperty(proto, property, {
        get: origDesc.get ? function(this: object) { return origDesc.get!.call(this) as unknown; } : undefined,
        set: function (this: PositionGizmo | RotationGizmo | ScaleGizmo, value: unknown) {
            origDesc.set!.call(this, value);
            if (value && 'onDragStartObservable' in this) {
                hookGizmoInstance(this);
            }
        },
        configurable: true,
        enumerable: true,
    });
}

function unpatchAll(): void {
    for (const { originalDescriptor, prototype, property } of patches) {
        Object.defineProperty(prototype, property, originalDescriptor);
    }
    patches.length = 0;

    // Clean up all observer subscriptions
    for (const [_gizmo, observers] of hookedInstances) {
        observers.dragStart.remove();
        for (const obs of observers.dragEnd) {
            obs.remove();
        }
    }
    hookedInstances.clear();
}

// ============================================================================
// Service Definition
// ============================================================================

export const UndoGizmoServiceDefinition: ServiceDefinition<[], [IGizmoService]> = {
    friendlyName: 'Undo Gizmo Service',

    consumes: [GizmoServiceIdentity],

    factory: (_gizmoService: IGizmoService) => {
        // Patch all gizmo classes to hook drag observables
        patchGizmoClass(PositionGizmo, 'attachedNode');
        patchGizmoClass(PositionGizmo, 'attachedMesh');
        patchGizmoClass(RotationGizmo, 'attachedNode');
        patchGizmoClass(RotationGizmo, 'attachedMesh');
        patchGizmoClass(ScaleGizmo, 'attachedNode');
        patchGizmoClass(ScaleGizmo, 'attachedMesh');
        // BoundingBoxGizmo inherits attachedMesh/attachedNode from Gizmo (no own
        // property descriptor), so prototype patching can't intercept it. Instead,
        // bounding-box-gizmo-service.ts calls hookGizmoInstance() directly.

        return {
            dispose: () => {
                unpatchAll();
            },
        };
    },
};
